import { useState, useEffect } from 'react';
import { FileText, Building2, Users, TrendingUp, Truck, CheckCircle, Clock, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { PageHeader } from '../components/common/PageHeader';
import { StatsCard } from '../components/common/StatsCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { getDashboardStats, DashboardStatsDto } from '../api-clients';
import { DashboardMetrics } from '../types';
import { useToast } from '../hooks/use-toast';
import { getErrorToast } from '../lib/error-utils';

const COLORS = ['hsl(152, 69%, 40%)', 'hsl(38, 92%, 50%)', 'hsl(217, 91%, 60%)'];

/**
 * Calculate trend percentage change
 * @param current Current value
 * @param previous Previous period value
 * @param isPositiveIncrease Whether an increase is considered positive (default: true)
 * @returns Trend object with percentage and direction, or undefined if trend can't be calculated
 */
const calculateTrend = (
  current: number,
  previous: number | undefined,
  isPositiveIncrease: boolean = true
): { value: number; isPositive: boolean } | undefined => {
  if (previous === undefined || previous === 0) {
    return undefined;
  }

  const percentageChange = ((current - previous) / previous) * 100;
  const isPositive = isPositiveIncrease 
    ? percentageChange >= 0 
    : percentageChange <= 0;

  return {
    value: Math.round(Math.abs(percentageChange) * 10) / 10, // Round to 1 decimal place
    isPositive,
  };
};

export default function Dashboard() {
  const { toast } = useToast();
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalEcmrs: 0,
    deliveredEcmrs: 0,
    pendingEcmrs: 0,
    inTransitEcmrs: 0,
    totalCompanies: 0,
    totalEmployees: 0,
    ecmrsByMonth: [],
    ecmrsByStatus: [],
  });
  const [dashboardData, setDashboardData] = useState<DashboardStatsDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data: DashboardStatsDto = await getDashboardStats();
      setDashboardData(data);
      
      // Map backend data to frontend DashboardMetrics format
      setMetrics({
        totalEcmrs: data.totalDocuments,
        deliveredEcmrs: data.deliveredDocuments,
        pendingEcmrs: data.pendingDocuments,
        inTransitEcmrs: data.inTransitDocuments,
        totalCompanies: data.totalCompanies,
        totalEmployees: data.totalEmployees,
        ecmrsByMonth: data.ecmrsByMonth.map(m => ({ month: m.month, count: m.count })),
        ecmrsByStatus: data.ecmrsByStatus.map(s => ({ status: s.status, count: s.count })),
      });
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao carregar dados do dashboard');
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        title="Dashboard"
        description="Visão geral do sistema eCMR"
      />

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total eCMRs"
          value={metrics.totalEcmrs}
          icon={<FileText className="h-6 w-6 text-primary" />}
          trend={dashboardData && dashboardData.ecmrsByMonth.length >= 2 
            ? (() => {
                // Compare last month vs previous month from monthly data
                const months = dashboardData.ecmrsByMonth;
                const lastMonth = months[months.length - 1]?.count || 0;
                const previousMonth = months[months.length - 2]?.count || 0;
                return calculateTrend(lastMonth, previousMonth, true);
              })()
            : undefined}
          delay={0}
        />
        <StatsCard
          title="Entregues"
          value={metrics.deliveredEcmrs}
          icon={<CheckCircle className="h-6 w-6 text-status-delivered" />}
          trend={dashboardData ? calculateTrend(
            dashboardData.deliveredDocuments,
            dashboardData.previousDeliveredDocuments,
            true
          ) : undefined}
          delay={100}
        />
        <StatsCard
          title="Pendentes"
          value={metrics.pendingEcmrs}
          icon={<Clock className="h-6 w-6 text-status-pending" />}
          trend={dashboardData ? calculateTrend(
            dashboardData.pendingDocuments,
            dashboardData.previousPendingDocuments,
            false // Decrease in pending is positive
          ) : undefined}
          delay={200}
        />
        <StatsCard
          title="Em Trânsito"
          value={metrics.inTransitEcmrs}
          icon={<Truck className="h-6 w-6 text-status-transit" />}
          trend={dashboardData ? calculateTrend(
            dashboardData.inTransitDocuments,
            dashboardData.previousInTransitDocuments,
            true
          ) : undefined}
          delay={300}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid gap-4 sm:grid-cols-2">
        <StatsCard
          title="Empresas Registadas"
          value={metrics.totalCompanies}
          icon={<Building2 className="h-6 w-6 text-primary" />}
          trend={dashboardData ? calculateTrend(
            dashboardData.totalCompanies,
            dashboardData.previousTotalCompanies,
            true
          ) : undefined}
          delay={400}
        />
        <StatsCard
          title="Colaboradores Ativos"
          value={metrics.totalEmployees}
          icon={<Users className="h-6 w-6 text-primary" />}
          trend={dashboardData ? calculateTrend(
            dashboardData.totalEmployees,
            dashboardData.previousTotalEmployees,
            true
          ) : undefined}
          delay={500}
        />
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Bar Chart */}
        <Card className="animate-slide-up" style={{ animationDelay: '600ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-accent" />
              eCMRs por Mês
            </CardTitle>
            <CardDescription>Evolução dos últimos 6 meses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics.ecmrsByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="month" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="hsl(var(--accent))" 
                    radius={[4, 4, 0, 0]}
                    name="eCMRs"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="animate-slide-up" style={{ animationDelay: '700ms' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-accent" />
              Distribuição por Estado
            </CardTitle>
            <CardDescription>Estado atual dos eCMRs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={metrics.ecmrsByStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={4}
                    dataKey="count"
                    nameKey="status"
                    label={({ status, count }) => `${status}: ${count}`}
                    labelLine={false}
                  >
                    {metrics.ecmrsByStatus.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex justify-center gap-6">
              {metrics.ecmrsByStatus.map((item, index) => (
                <div key={item.status} className="flex items-center gap-2">
                  <div 
                    className="h-3 w-3 rounded-full" 
                    style={{ backgroundColor: COLORS[index] }}
                  />
                  <span className="text-sm text-muted-foreground">{item.status}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
